import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable, catchError, throwError } from 'rxjs';

import { NotificationService } from '../notification/notification.service';

interface TenantMismatchDetail {
  code: 'tenant_subdomain_mismatch';
  reason: 'tenant_mismatch' | 'missing_tenant_subdomain';
  expected_tenant: string;
  requested_tenant?: string;
  message?: string;
}

/**
 * Maps backend HTTP errors to user-visible behaviour:
 *   401 -> bounce to /login
 *   403 with detail.code === 'tenant_subdomain_mismatch'
 *       -> toast naming the expected tenant, log out, bounce to /login
 *   403 (other) -> red toast "you don't have permission"
 *   5xx -> red toast "something went wrong"
 *
 * Errors are still propagated to the caller so feature code can react.
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  // Guard so the logout flow only fires once when several requests 403
  // in parallel (e.g. /v1/config + /v1/cases on first paint).
  private tenantMismatchHandled = false;

  constructor(
    private router: Router,
    private notify: NotificationService,
    private oauth: OAuthService,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          this.router.navigateByUrl('/login');
        } else if (err.status === 403) {
          const detail = this.readTenantMismatchDetail(err);
          if (detail) {
            this.handleTenantMismatch(detail);
          } else {
            this.notify.error("You don't have permission to do that.");
          }
        } else if (err.status >= 500) {
          this.notify.error('Something went wrong on the server. Please try again.');
        }
        return throwError(() => err);
      }),
    );
  }

  private readTenantMismatchDetail(err: HttpErrorResponse): TenantMismatchDetail | null {
    const detail = err.error?.detail;
    if (detail && typeof detail === 'object' && detail.code === 'tenant_subdomain_mismatch') {
      return detail as TenantMismatchDetail;
    }
    return null;
  }

  private handleTenantMismatch(detail: TenantMismatchDetail): void {
    if (this.tenantMismatchHandled) return;
    this.tenantMismatchHandled = true;

    const message =
      detail.reason === 'tenant_mismatch'
        ? `You're signed in to the wrong workspace. Your account belongs to "${detail.expected_tenant}". Signing you out…`
        : `Your account requires the "${detail.expected_tenant}" workspace. Signing you out…`;
    this.notify.error(message);

    // Give the toast a moment to render before tearing down the session.
    setTimeout(() => {
      try {
        this.oauth.logOut();
      } catch {
        // logOut() can throw if the OIDC discovery doc never loaded; we
        // still want to bounce the user to login in that case.
      }
      this.router.navigateByUrl('/login');
    }, 1500);
  }
}
